import { debounce, uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ClientManager, ObjectManager } from '../../../concepts/objects';
import { SessionStorageService, StorageService } from '../../../platform/StorageService';
import { AccountModel } from '../../account/AccountModel';
import { BoardUsageModel } from '../../account/BoardUsageModel';
import { ActiveObject, resolve } from '../../Container';
import { templateInsert } from '../../packages/objectLibrary/ObjectLibraryWidget';
import { PopupManager } from '../../popups/PopupManager';
import { PreferencesModel } from '../../preferences/PreferencesModel';
import { BoardInfo } from '../BoardInfo';
import { EditorModeInfo } from '../EditorModeInfo';
import { EditorOptions } from '../EditorOptions';
import { EditorSession } from '../EditorSession';
import { MyRoleInfo } from '../MyRoleInfo';
import { SuggestedWidgetScale } from '../workspace/SuggestedWidgetScaleProperty';
import { WidgetManager } from '../workspace/widgets/WidgetManager';
import { DesignMode } from './DesignMode';
import { switchToDesignMode } from './DesignModeCommands';
import { EditorModeMenuWidget } from './EditorModeMenuWidget';
import { editorModePopup } from './EditorModePopup';
import { IsElevated } from './IsElevated';
import { MeetingMode } from './MeetingMode';
import { switchToMeetingMode } from './MeetingModeCommands';
import { resolveActiveMeeting } from './MeetingUtil';
import { ViewMode } from './ViewMode';

export class EditorModeMechanics extends ActiveObject {

    @resolve(AccountModel) private account!: AccountModel;
    @resolve(BoardUsageModel) private boardLimit!: BoardUsageModel;
    @resolve(ClientManager) private clients!: ClientManager;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(EditorOptions) private options!: EditorOptions;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(PopupManager) private popups!: PopupManager;
    @resolve(PreferencesModel) private preferences!: PreferencesModel;
    @resolve(SessionStorageService) private tempData!: StorageService;
    @resolve(WidgetManager) private widgets!: WidgetManager;

    public start() {
        const syncDebounced = debounce(() => this.sync());
        this.burden(
            // On Meeting create force even hosts into MeetingMode
            this.objects.on('Meeting', 'allocated', () => this.sync(true)),
            this.objects.on('Meeting', 'affected', syncDebounced),
            this.objects.on('Meeting', 'released', syncDebounced),
            this.session.observe(EditorModeInfo, syncDebounced),
            this.session.observe(SuggestedWidgetScale, syncDebounced),
            this.session.observe(IsElevated, syncDebounced),
            this.session.observe(MyRoleInfo, syncDebounced),
        );
        this.sync(true);
        this.invokeEditorModePopupIfApplicable();
    }

    private sync = (ignoreHostPriv?: boolean) => {

        const prev = this.session.get(EditorModeInfo);
        const next = this.getNextMode(prev, ignoreHostPriv);
        if (prev.id !== next.id) {
            this.session.patch(EditorModeInfo, next);
        }

        this.realizeEditorModeWidgetIfApplicable();
    };

    private getNextMode(from: EditorModeInfo, ignoreHostPriv: boolean) {
        const board = this.session.get(BoardInfo);
        const meeting = resolveActiveMeeting(this.objects);
        const isHost = this.session.get(IsElevated);
        const { canParticipate } = this.session.get(MyRoleInfo);

        if (this.account.plan.features.readonly) {
            return ViewMode;
        }

        if (!this.boardLimit.isBoardActive(board.id)) {
            return ViewMode;
        }

        if (!canParticipate) {
            return ViewMode;
        }

        if (!!meeting && (ignoreHostPriv || !isHost || from.id === ViewMode.id)) {
            return MeetingMode;
        }

        if (!meeting && from.id === MeetingMode.id) {
            return DesignMode;
        }

        if (from.id === ViewMode.id && this.options.action !== 'template') {
            return DesignMode;
        }

        return from;
    }

    private realizeEditorModeWidgetIfApplicable() {
        const mode = this.session.get(EditorModeInfo);

        this.widgets.delete('editorMode');
        if (mode.id === ViewMode.id) return;

        const dock = this.widgets.createContainer({
            name: 'editorMode',
            weight: 1000,
            position: ['docked', 'topLeft', 'stack'],
            direction: 'column',
            scale: this.computeContainerScale(),
        });

        dock.add(this.widgets.createPopout({
            text: 'Switch Mode',
            image: 'deqo://icons/meeting/switch-mode',
            ui: EditorModeMenuWidget,
            autoClose: true,
        }));

        this.widgets.add(dock);
    }

    private invokeEditorModePopupIfApplicable = () => {
        const board = this.session.get(BoardInfo);
        const hasShown = () => this.tempData.get(`deqo/ui/didShowEditorModePopup/${board.id}`);
        const setShown = () => this.tempData.put(`deqo/ui/didShowEditorModePopup/${board.id}`, true);
        if (this.options.action === 'template' && !hasShown()) {
            if (this.preferences.editorModePopup) {
                this.scheduler.timeout(uuid(), 1000, async () => {
                    const { actioned, data } = await this.popups.invoke(editorModePopup());
                    setShown();
                    if (actioned && data === MeetingMode.id) {
                        this.dispatch(switchToMeetingMode);
                    }
                    else {
                        this.dispatch(switchToDesignMode);
                    }
                });
            }
            else {
                this.dispatch(switchToDesignMode);
            }
        }
        if (this.options.action?.startsWith('template:')) {
            this.scheduler.timeout(uuid(), 500, () => {
                const [_, tag] = this.options.action.split(':');
                this.dispatch(templateInsert, [tag]);
            });

        }
    };

    private computeContainerScale = () => {
        const sws = this.session.get(SuggestedWidgetScale);
        if (sws === 'smallest') return 1.25;
        if (sws === 'small') return 1.5;
        else return 2;
    };

    private getParticipantLimit = () => {
        return this.account.plan.features.participantLimit || 1000;
    };
}
